import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Slider from '@react-native-community/slider';
import { Svg, Circle, Text as SvgText, Line, Rect, Path } from 'react-native-svg';
import Footer from '../components/Footer';
import { useTheme } from '../contexts/ThemeContext';

const { width: screenWidth } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = screenWidth < 768;

const HRACalculatorScreen = () => {
  const { colors } = useTheme();
  const [basicSalary, setBasicSalary] = useState('50000');
  const [hraReceived, setHraReceived] = useState('20000');
  const [rentPaid, setRentPaid] = useState('18000');
  const [cityType, setCityType] = useState('metro');
  const [taxSlab, setTaxSlab] = useState('30');
  const [showComparison, setShowComparison] = useState(false);
  const [showDocumentation, setShowDocumentation] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [results, setResults] = useState(null);

  const cityTypes = [
    {
      type: 'metro',
      name: 'Metro Cities',
      description: 'Delhi, Mumbai, Chennai, Kolkata',
      exemptionRate: 50,
      color: colors.primary,
    },
    {
      type: 'non-metro',
      name: 'Non-Metro Cities',
      description: 'All other cities',
      exemptionRate: 40,
      color: colors.success,
    },
  ];

  const taxSlabs = [
    { rate: '5', label: '5%', description: 'Income: ₹2.5L - ₹5L' },
    { rate: '20', label: '20%', description: 'Income: ₹5L - ₹10L' },
    { rate: '30', label: '30%', description: 'Income: Above ₹10L' },
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const calculateHRA = () => {
    const basic = parseFloat(basicSalary) || 0;
    const hraRec = parseFloat(hraReceived) || 0;
    const rent = parseFloat(rentPaid) || 0;
    const taxRate = parseFloat(taxSlab) || 0;

    // Get city exemption rate
    const cityData = cityTypes.find(city => city.type === cityType);
    const exemptionRate = cityData ? cityData.exemptionRate : 50;

    // Calculate HRA exemption as per IT Act Section 10(13A)
    // Minimum of:
    // 1. Actual HRA received
    // 2. 50% of basic salary (metro) or 40% (non-metro)
    // 3. Rent paid minus 10% of basic salary

    const actualHRA = hraRec;
    const percentageOfBasic = (basic * exemptionRate) / 100;
    const rentMinus10Percent = Math.max(0, rent - (basic * 0.1));

    const hraExemption = Math.min(actualHRA, percentageOfBasic, rentMinus10Percent);
    const taxableHRA = Math.max(0, hraRec - hraExemption);

    // Calculate tax savings
    const taxSavings = hraExemption * (taxRate / 100);
    const effectiveTaxRate = taxableHRA > 0 ? (taxableHRA * taxRate / 100) / hraRec * 100 : 0;

    // Calculate annual figures
    const annualBasic = basic * 12;
    const annualHRA = hraRec * 12;
    const annualRent = rent * 12;
    const annualExemption = hraExemption * 12;
    const annualTaxSavings = taxSavings * 12;

    // Calculate different scenarios
    const scenarios = [
      {
        name: 'Current Structure',
        basic: basic,
        hra: hraRec,
        rent: rent,
        exemption: hraExemption,
        taxSavings: taxSavings,
      },
      {
        name: 'Without HRA',
        basic: basic + hraRec,
        hra: 0,
        rent: rent,
        exemption: 0,
        taxSavings: 0,
      },
      {
        name: 'Optimized Rent',
        basic: basic,
        hra: hraRec,
        rent: basic * 0.1 + percentageOfBasic,
        exemption: Math.min(hraRec, percentageOfBasic),
        taxSavings: Math.min(hraRec, percentageOfBasic) * (taxRate / 100),
      },
    ];

    return {
      basic,
      hraReceived: hraRec,
      rentPaid: rent,
      exemptionRate,
      actualHRA,
      percentageOfBasic,
      rentMinus10Percent,
      hraExemption,
      taxableHRA,
      taxSavings,
      effectiveTaxRate,
      annualBasic,
      annualHRA,
      annualRent,
      annualExemption,
      annualTaxSavings,
      scenarios,
    };
  };

  useEffect(() => {
    const calculatedResults = calculateHRA();
    setResults(calculatedResults);
  }, [basicSalary, hraReceived, rentPaid, cityType, taxSlab]);

  const renderHeader = () => (
    <View style={styles.header}>
      <Text style={styles.headerTitle}>HRA Calculator</Text>
      <Text style={styles.headerSubtitle}>
        Calculate your House Rent Allowance exemption and tax savings as per Section 10(13A) of Income Tax Act
      </Text>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorCard}>
      <Text style={styles.cardTitle}>HRA Details</Text>
      
      {/* Basic Salary */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Monthly Basic Salary</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={basicSalary}
            onChangeText={setBasicSalary}
            placeholder="50000"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Enter your monthly basic salary (excluding allowances)</Text>
      </View>

      {/* HRA Received */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Monthly HRA Received</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={hraReceived}
            onChangeText={setHraReceived}
            placeholder="20000"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>HRA component in your salary structure</Text>
      </View>

      {/* Rent Paid */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Monthly Rent Paid</Text>
        <View style={styles.inputContainer}>
          <Text style={styles.currencySymbol}>₹</Text>
          <TextInput
            style={styles.textInput}
            value={rentPaid}
            onChangeText={setRentPaid}
            placeholder="18000"
            keyboardType="numeric"
          />
        </View>
        <Text style={styles.helperText}>Actual rent paid for accommodation</Text>
      </View>

      {/* City Type */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>City Type</Text>
        <View style={styles.cityTypeContainer}>
          {cityTypes.map((city) => (
            <TouchableOpacity
              key={city.type}
              style={[
                styles.cityTypeCard,
                cityType === city.type && styles.cityTypeCardActive,
              ]}
              onPress={() => setCityType(city.type)}
            >
              <View style={[styles.cityTypeIndicator, { backgroundColor: city.color }]} />
              <Text style={[
                styles.cityTypeName,
                cityType === city.type && styles.cityTypeNameActive,
              ]}>
                {city.name}
              </Text>
              <Text style={[
                styles.cityTypeDescription,
                cityType === city.type && styles.cityTypeDescriptionActive,
              ]}>
                {city.description}
              </Text>
              <Text style={[
                styles.cityTypeRate,
                cityType === city.type && styles.cityTypeRateActive,
              ]}>
                {city.exemptionRate}% of Basic
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Tax Slab */}
      <View style={styles.inputGroup}>
        <Text style={styles.label}>Your Tax Slab</Text>
        <View style={styles.taxSlabContainer}>
          {taxSlabs.map((slab) => (
            <TouchableOpacity
              key={slab.rate}
              style={[
                styles.taxSlabButton,
                taxSlab === slab.rate && styles.taxSlabButtonActive,
              ]}
              onPress={() => setTaxSlab(slab.rate)}
            >
              <Text style={[
                styles.taxSlabLabel,
                taxSlab === slab.rate && styles.taxSlabLabelActive,
              ]}>
                {slab.label}
              </Text>
              <Text style={[
                styles.taxSlabDescription,
                taxSlab === slab.rate && styles.taxSlabDescriptionActive,
              ]}>
                {slab.description}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </View>
  );

  const renderDonutChart = () => {
    if (!results) return null;

    const { hraReceived, hraExemption, taxableHRA } = results;
    const exemptionPercentage = hraReceived > 0 ? (hraExemption / hraReceived) * 100 : 0;
    const taxablePercentage = hraReceived > 0 ? (taxableHRA / hraReceived) * 100 : 0;

    const size = 200;
    const strokeWidth = 20;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;

    const exemptionStrokeDasharray = `${(exemptionPercentage / 100) * circumference} ${circumference}`;
    const taxableStrokeDasharray = `${(taxablePercentage / 100) * circumference} ${circumference}`;
    const taxableStrokeDashoffset = -((exemptionPercentage / 100) * circumference);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.cardTitle}>HRA Breakdown</Text>
        <View style={styles.chartContainer}>
          <Svg width={size} height={size}>
            {/* Background circle */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.border}
              strokeWidth={strokeWidth}
              fill="transparent"
            />
            
            {/* Exempted amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.success}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={exemptionStrokeDasharray}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Taxable amount */}
            <Circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              stroke={colors.error}
              strokeWidth={strokeWidth}
              fill="transparent"
              strokeDasharray={taxableStrokeDasharray}
              strokeDashoffset={taxableStrokeDashoffset}
              strokeLinecap="round"
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
            
            {/* Center text */}
            <SvgText
              x={size / 2}
              y={size / 2 - 10}
              textAnchor="middle"
              fontSize="14"
              fontWeight="600"
              fill={colors.text}
            >
              Monthly HRA
            </SvgText>
            <SvgText
              x={size / 2}
              y={size / 2 + 10}
              textAnchor="middle"
              fontSize="16"
              fontWeight="bold"
              fill={colors.text}
            >
              {formatCurrency(hraReceived)}
            </SvgText>
          </Svg>
        </View>
        
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.success }]} />
            <Text style={styles.legendText}>Exempted ({exemptionPercentage.toFixed(1)}%)</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, { backgroundColor: colors.error }]} />
            <Text style={styles.legendText}>Taxable ({taxablePercentage.toFixed(1)}%)</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderResults = () => {
    if (!results) return null;

    const { 
      basic,
      hraReceived,
      rentPaid,
      exemptionRate,
      actualHRA,
      percentageOfBasic,
      rentMinus10Percent,
      hraExemption,
      taxableHRA,
      taxSavings,
      annualExemption,
      annualTaxSavings
    } = results;

    return (
      <View style={styles.resultsCard}>
        <Text style={styles.cardTitle}>Calculation Results</Text>
        
        <View style={styles.calculationSteps}>
          <Text style={styles.stepsTitle}>HRA Exemption Calculation (Minimum of):</Text>
          
          <View style={styles.stepItem}>
            <Text style={styles.stepLabel}>1. Actual HRA Received</Text>
            <Text style={styles.stepValue}>{formatCurrency(actualHRA)}</Text>
          </View>
          
          <View style={styles.stepItem}>
            <Text style={styles.stepLabel}>2. {exemptionRate}% of Basic Salary</Text>
            <Text style={styles.stepValue}>{formatCurrency(percentageOfBasic)}</Text>
          </View>
          
          <View style={styles.stepItem}>
            <Text style={styles.stepLabel}>3. Rent - 10% of Basic</Text>
            <Text style={styles.stepValue}>{formatCurrency(rentMinus10Percent)}</Text>
          </View>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Monthly HRA Exemption</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(hraExemption)}
          </Text>
        </View>
        
        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Monthly Taxable HRA</Text>
          <Text style={[styles.resultValue, { color: colors.error }]}>
            {formatCurrency(taxableHRA)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Monthly Tax Savings</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(taxSavings)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Annual HRA Exemption</Text>
          <Text style={[styles.resultValue, { color: colors.success }]}>
            {formatCurrency(annualExemption)}
          </Text>
        </View>

        <View style={styles.resultItem}>
          <Text style={styles.resultLabel}>Annual Tax Savings</Text>
          <Text style={[styles.resultValue, { color: colors.success, fontSize: 18, fontWeight: 'bold' }]}>
            {formatCurrency(annualTaxSavings)}
          </Text>
        </View>
      </View>
    );
  };

  const renderComparison = () => {
    if (!results) return null;

    const { scenarios } = results;

    return (
      <View style={styles.comparisonCard}>
        <TouchableOpacity
          style={styles.cardHeader}
          onPress={() => setShowComparison(!showComparison)}
        >
          <Text style={styles.cardTitle}>Scenario Comparison</Text>
          <Ionicons
            name={showComparison ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showComparison && (
          <View style={styles.cardContent}>
            {scenarios.map((scenario, index) => (
              <View key={index} style={styles.scenarioItem}>
                <View style={styles.scenarioHeader}>
                  <Text style={styles.scenarioName}>{scenario.name}</Text>
                  <Text style={styles.scenarioSavings}>
                    Savings: {formatCurrency(scenario.taxSavings * 12)}/year
                  </Text>
                </View>
                <View style={styles.scenarioDetails}>
                  <Text style={styles.scenarioDetail}>
                    Basic: {formatCurrency(scenario.basic)} | 
                    HRA: {formatCurrency(scenario.hra)} | 
                    Rent: {formatCurrency(scenario.rent)}
                  </Text>
                  <Text style={styles.scenarioExemption}>
                    Exemption: {formatCurrency(scenario.exemption)}
                  </Text>
                </View>
                <View style={styles.scenarioBar}>
                  <View 
                    style={[
                      styles.scenarioBarFill, 
                      { 
                        width: `${(scenario.taxSavings / Math.max(...scenarios.map(s => s.taxSavings))) * 100}%`,
                        backgroundColor: index === 0 ? colors.primary : index === 1 ? colors.error : colors.success
                      }
                    ]} 
                  />
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderDocumentation = () => (
    <View style={styles.documentationCard}>
      <TouchableOpacity
        style={styles.cardHeader}
        onPress={() => setShowDocumentation(!showDocumentation)}
      >
        <Text style={styles.cardTitle}>Documentation Requirements</Text>
        <Ionicons
          name={showDocumentation ? 'chevron-up' : 'chevron-down'}
          size={24}
          color={colors.primary}
        />
      </TouchableOpacity>
      
      {showDocumentation && (
        <View style={styles.cardContent}>
          <View style={styles.docSection}>
            <Text style={styles.docSectionTitle}>Required Documents:</Text>
            <View style={styles.docList}>
              <Text style={styles.docItem}>• Rent receipts for amounts above ₹1 lakh annually</Text>
              <Text style={styles.docItem}>• Rental agreement copy</Text>
              <Text style={styles.docItem}>• Landlord's PAN card (if rent > ₹1 lakh/year)</Text>
              <Text style={styles.docItem}>• Property tax receipts (if available)</Text>
            </View>
          </View>

          <View style={styles.docSection}>
            <Text style={styles.docSectionTitle}>Important Notes:</Text>
            <View style={styles.docList}>
              <Text style={styles.docItem}>• HRA exemption is available only if you live in a rented house</Text>
              <Text style={styles.docItem}>• You cannot claim HRA if you own the house you live in</Text>
              <Text style={styles.docItem}>• Rent receipts must be in your name</Text>
              <Text style={styles.docItem}>• Keep all documents for at least 6 years</Text>
            </View>
          </View>

          <View style={styles.tipBox}>
            <Ionicons name="bulb" size={16} color={colors.warning} />
            <Text style={styles.tipText}>
              <Text style={{ fontWeight: 'bold' }}>Pro Tip:</Text> If your annual rent exceeds ₹1 lakh, 
              ensure you have your landlord's PAN card details to claim HRA exemption.
            </Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderInformation = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.infoTitle}>About HRA Exemption</Text>
      <Text style={styles.infoSubtitle}>Understanding House Rent Allowance Benefits</Text>
      
      <Text style={styles.infoText}>
        HRA (House Rent Allowance) is a component of salary that provides tax exemption under 
        Section 10(13A) of the Income Tax Act. The exemption is calculated as the minimum of 
        three components to ensure fair tax benefits for genuine rental expenses.
      </Text>
      
      <View style={styles.tipBox}>
        <Ionicons name="home" size={16} color={colors.primary} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Metro vs Non-Metro:</Text> Metro cities (Delhi, Mumbai, 
          Chennai, Kolkata) get 50% exemption while non-metro cities get 40% of basic salary.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="calculator" size={16} color={colors.success} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Optimization:</Text> Structure your salary to maximize 
          HRA component and ensure rent paid is optimal for maximum tax savings.
        </Text>
      </View>
      
      <View style={styles.tipBox}>
        <Ionicons name="document-text" size={16} color={colors.warning} />
        <Text style={styles.tipText}>
          <Text style={{ fontWeight: 'bold' }}>Documentation:</Text> Maintain proper rent receipts 
          and rental agreements. PAN card of landlord is mandatory for rent above ₹1 lakh annually.
        </Text>
      </View>
    </View>
  );

  const renderSEOContent = () => (
    <View style={styles.infoContainer}>
      <Text style={styles.seoTitle}>🏠 Complete Guide to HRA Calculator & Tax Savings</Text>
      <Text style={styles.seoSubtitle}>Master House Rent Allowance Calculations for Maximum Tax Benefits</Text>
      
      <Text style={styles.seoText}>
        Our advanced HRA Calculator helps you determine the exact tax exemption available under Section 10(13A) 
        of the Income Tax Act. Calculate your House Rent Allowance benefits, optimize your salary structure, 
        and maximize tax savings with our comprehensive tool designed for Indian taxpayers.
      </Text>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📊 What is HRA and How Does It Work?</Text>
        <Text style={styles.seoText}>
          House Rent Allowance (HRA) is a salary component that provides tax exemption for employees living 
          in rented accommodation. The exemption is calculated as the minimum of three components:
        </Text>
        
        <View style={styles.comparisonContainer}>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>🏢 Metro Cities</Text>
            <Text style={styles.comparisonText}>• 50% of Basic Salary</Text>
            <Text style={styles.comparisonText}>• Delhi, Mumbai, Chennai, Kolkata</Text>
            <Text style={styles.comparisonText}>• Higher exemption rate</Text>
            <Text style={styles.comparisonText}>• Better tax savings potential</Text>
          </View>
          
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>🏘️ Non-Metro Cities</Text>
            <Text style={styles.comparisonText}>• 40% of Basic Salary</Text>
            <Text style={styles.comparisonText}>• All other cities in India</Text>
            <Text style={styles.comparisonText}>• Standard exemption rate</Text>
            <Text style={styles.comparisonText}>• Significant tax benefits</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🎯 Key Features of Our HRA Calculator</Text>
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Ionicons name="calculator-outline" size={20} color={colors.primary} />
            <Text style={styles.featureText}>Accurate HRA exemption calculation as per IT Act Section 10(13A)</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="location-outline" size={20} color={colors.primary} />
            <Text style={styles.featureText}>Metro vs Non-Metro city classification for correct exemption rates</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="trending-up-outline" size={20} color={colors.primary} />
            <Text style={styles.featureText}>Tax savings calculation across different income slabs</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="analytics-outline" size={20} color={colors.primary} />
            <Text style={styles.featureText}>Scenario comparison for salary optimization</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="document-text-outline" size={20} color={colors.primary} />
            <Text style={styles.featureText}>Documentation requirements and compliance guidelines</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="pie-chart-outline" size={20} color={colors.primary} />
            <Text style={styles.featureText}>Visual breakdown of taxable vs exempt HRA components</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📋 HRA Exemption Calculation Formula</Text>
        <Text style={styles.seoText}>
          The HRA exemption is calculated as the minimum of the following three amounts:
        </Text>
        
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>1</Text>
            </View>
            <Text style={styles.stepText}>
              <Text style={{ fontWeight: 'bold' }}>Actual HRA Received:</Text> The actual amount of HRA 
              component in your salary as per your employment contract.
            </Text>
          </View>
          
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>2</Text>
            </View>
            <Text style={styles.stepText}>
              <Text style={{ fontWeight: 'bold' }}>Percentage of Basic Salary:</Text> 50% of basic salary 
              for metro cities or 40% for non-metro cities.
            </Text>
          </View>
          
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>3</Text>
            </View>
            <Text style={styles.stepText}>
              <Text style={{ fontWeight: 'bold' }}>Rent Minus 10% of Basic:</Text> Annual rent paid minus 
              10% of annual basic salary (excess rent calculation).
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📝 How to Use the HRA Calculator</Text>
        <View style={styles.stepsList}>
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>1</Text>
            </View>
            <Text style={styles.stepText}>Enter your monthly basic salary (excluding allowances and bonuses)</Text>
          </View>
          
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>2</Text>
            </View>
            <Text style={styles.stepText}>Input the monthly HRA amount received from your employer</Text>
          </View>
          
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>3</Text>
            </View>
            <Text style={styles.stepText}>Enter the monthly rent you pay for your accommodation</Text>
          </View>
          
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>4</Text>
            </View>
            <Text style={styles.stepText}>Select your city type (Metro or Non-Metro) for correct exemption rate</Text>
          </View>
          
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>5</Text>
            </View>
            <Text style={styles.stepText}>Choose your tax slab to calculate accurate tax savings</Text>
          </View>
          
          <View style={styles.stepItem}>
            <View style={styles.stepNumber}>
              <Text style={styles.stepNumberText}>6</Text>
            </View>
            <Text style={styles.stepText}>Review the calculated exemption, tax savings, and optimization suggestions</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>💡 HRA Optimization Strategies</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <Ionicons name="bulb-outline" size={20} color={colors.warning} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Salary Structure Optimization</Text>
              <Text style={styles.strategyText}>
                Negotiate with your employer to increase the HRA component in your salary structure. 
                A higher HRA component can lead to significant tax savings if you pay substantial rent.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <Ionicons name="home-outline" size={20} color={colors.success} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Rent Amount Planning</Text>
              <Text style={styles.strategyText}>
                Plan your rent payments to optimize HRA exemption. Ensure rent exceeds 10% of basic salary 
                to maximize the third component of HRA calculation.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <Ionicons name="document-outline" size={20} color={colors.primary} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Documentation Management</Text>
              <Text style={styles.strategyText}>
                Maintain proper rent receipts, rental agreements, and landlord's PAN card details. 
                Good documentation ensures smooth tax filing and exemption claims.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <Ionicons name="location-outline" size={20} color={colors.error} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>City Classification Benefits</Text>
              <Text style={styles.strategyText}>
                If working in metro cities, leverage the higher 50% exemption rate. Consider job locations 
                when evaluating overall compensation packages.
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>⚖️ Legal Requirements & Compliance</Text>
        <Text style={styles.seoText}>
          To claim HRA exemption under Section 10(13A), you must comply with specific legal requirements:
        </Text>
        
        <View style={styles.featuresList}>
          <View style={styles.featureItem}>
            <Ionicons name="checkmark-circle-outline" size={20} color={colors.success} />
            <Text style={styles.featureText}>Must live in rented accommodation (not self-owned property)</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="checkmark-circle-outline" size={20} color={colors.success} />
            <Text style={styles.featureText}>HRA component must be part of your salary structure</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="checkmark-circle-outline" size={20} color={colors.success} />
            <Text style={styles.featureText}>Rent receipts mandatory for amounts above ₹3,000 per month</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="checkmark-circle-outline" size={20} color={colors.success} />
            <Text style={styles.featureText}>Landlord's PAN required for annual rent exceeding ₹1 lakh</Text>
          </View>
          <View style={styles.featureItem}>
            <Ionicons name="checkmark-circle-outline" size={20} color={colors.success} />
            <Text style={styles.featureText}>Rental agreement should be in your name</Text>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>🔍 Common HRA Calculation Mistakes to Avoid</Text>
        <View style={styles.strategiesList}>
          <View style={styles.strategyItem}>
            <Ionicons name="warning-outline" size={20} color={colors.error} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Including Gross Salary Instead of Basic</Text>
              <Text style={styles.strategyText}>
                Always use basic salary for HRA calculations, not gross salary. Including allowances 
                and bonuses will lead to incorrect exemption calculations.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <Ionicons name="warning-outline" size={20} color={colors.error} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Wrong City Classification</Text>
              <Text style={styles.strategyText}>
                Ensure correct metro/non-metro classification. Using wrong rates can significantly 
                impact your tax savings and exemption calculations.
              </Text>
            </View>
          </View>
          
          <View style={styles.strategyItem}>
            <Ionicons name="warning-outline" size={20} color={colors.error} />
            <View style={styles.strategyContent}>
              <Text style={styles.strategyTitle}>Inadequate Documentation</Text>
              <Text style={styles.strategyText}>
                Missing rent receipts or landlord's PAN can lead to exemption rejection. Maintain 
                complete documentation throughout the financial year.
              </Text>
            </View>
          </View>
        </View>
      </View>

      <View style={styles.seoSection}>
        <Text style={styles.seoSectionTitle}>📈 Tax Savings Examples</Text>
        <Text style={styles.seoText}>
          Here are practical examples showing potential tax savings with proper HRA planning:
        </Text>
        
        <View style={styles.comparisonContainer}>
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>💼 Example 1: Metro Employee</Text>
            <Text style={styles.comparisonText}>Basic Salary: ₹60,000/month</Text>
            <Text style={styles.comparisonText}>HRA Received: ₹25,000/month</Text>
            <Text style={styles.comparisonText}>Rent Paid: ₹22,000/month</Text>
            <Text style={styles.comparisonText}>Tax Slab: 30%</Text>
            <Text style={styles.comparisonText}>Annual Savings: ₹66,000</Text>
          </View>
          
          <View style={styles.comparisonItem}>
            <Text style={styles.comparisonTitle}>🏘️ Example 2: Non-Metro Employee</Text>
            <Text style={styles.comparisonText}>Basic Salary: ₹45,000/month</Text>
            <Text style={styles.comparisonText}>HRA Received: ₹15,000/month</Text>
            <Text style={styles.comparisonText}>Rent Paid: ₹12,000/month</Text>
            <Text style={styles.comparisonText}>Tax Slab: 20%</Text>
            <Text style={styles.comparisonText}>Annual Savings: ₹21,600</Text>
          </View>
        </View>
      </View>
    </View>
  );

  const renderFAQ = () => {
    const faqData = [
      {
        question: "Can I claim HRA if I live in my own house?",
        answer: "No, HRA exemption is only available if you live in a rented accommodation. You cannot claim HRA for a house that you own, even if you have an HRA component in your salary."
      },
      {
        question: "What if I don't have rent receipts?",
        answer: "Rent receipts are mandatory for claiming HRA exemption. For annual rent above ₹1 lakh, you also need your landlord's PAN card details. Without proper documentation, you cannot claim the exemption."
      },
      {
        question: "Can I claim HRA and home loan interest together?",
        answer: "Yes, you can claim both HRA exemption (for rented house) and home loan interest deduction (for owned house) if you live in a rented house in one city and own a house in another city."
      },
      {
        question: "How is the 10% of basic salary calculated?",
        answer: "The 10% of basic salary is deducted from the rent paid to calculate one component of HRA exemption. This ensures that only the excess rent (above 10% of basic) is considered for exemption calculation."
      },
      {
        question: "What happens if my company doesn't provide HRA?",
        answer: "If your company doesn't provide HRA as a salary component, you cannot claim HRA exemption. However, you might be able to restructure your salary to include HRA component for tax benefits."
      }
    ];

    return (
      <View style={styles.faqContainer}>
        <TouchableOpacity
          style={styles.faqHeader}
          onPress={() => setShowFAQ(!showFAQ)}
        >
          <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
          <Ionicons
            name={showFAQ ? 'chevron-up' : 'chevron-down'}
            size={24}
            color={colors.primary}
          />
        </TouchableOpacity>
        
        {showFAQ && (
          <View style={styles.faqContent}>
            {faqData.map((faq, index) => (
              <View key={index} style={styles.faqItem}>
                <Text style={styles.faqQuestion}>{faq.question}</Text>
                <Text style={styles.faqAnswer}>{faq.answer}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      backgroundColor: colors.surface,
      paddingVertical: 40,
      paddingHorizontal: 20,
      alignItems: 'center',
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    headerTitle: {
      fontSize: 32,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    headerSubtitle: {
      fontSize: 16,
      color: colors.textSecondary,
      textAlign: 'center',
      lineHeight: 24,
    },
    content: {
      flexDirection: isMobile ? 'column' : 'row',
      padding: 20,
      gap: 20,
      width: '100%',
    },
    leftPanel: {
      flex: isMobile ? 1 : 1.2,
    },
    rightPanel: {
      flex: 1,
      gap: 20,
    },
    calculatorCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 20,
    },
    inputGroup: {
      marginBottom: 20,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    inputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    currencySymbol: {
      fontSize: 16,
      color: colors.textSecondary,
      paddingLeft: 12,
    },
    textInput: {
      flex: 1,
      height: 48,
      paddingHorizontal: 12,
      fontSize: 16,
      color: colors.text,
    },
    helperText: {
      fontSize: 12,
      color: colors.textSecondary,
      marginTop: 4,
    },
    cityTypeContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 12,
      marginTop: 8,
    },
    cityTypeCard: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 16,
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
    },
    cityTypeCardActive: {
      backgroundColor: colors.primary + '20',
      borderColor: colors.primary,
    },
    cityTypeIndicator: {
      width: 12,
      height: 12,
      borderRadius: 6,
      marginBottom: 8,
    },
    cityTypeName: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
      textAlign: 'center',
    },
    cityTypeNameActive: {
      color: colors.primary,
    },
    cityTypeDescription: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 4,
      textAlign: 'center',
    },
    cityTypeDescriptionActive: {
      color: colors.primary,
    },
    cityTypeRate: {
      fontSize: 11,
      color: colors.textSecondary,
      fontWeight: '500',
    },
    cityTypeRateActive: {
      color: colors.primary,
    },
    taxSlabContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 8,
      marginTop: 8,
    },
    taxSlabButton: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 12,
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      alignItems: 'center',
    },
    taxSlabButtonActive: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    taxSlabLabel: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    taxSlabLabelActive: {
      color: colors.background,
    },
    taxSlabDescription: {
      fontSize: 10,
      color: colors.textSecondary,
      textAlign: 'center',
    },
    taxSlabDescriptionActive: {
      color: colors.background,
    },
    chartCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
      alignItems: 'center',
    },
    chartContainer: {
      alignItems: 'center',
      marginVertical: 20,
    },
    chartLegend: {
      marginTop: 20,
      gap: 8,
    },
    legendItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    legendColor: {
      width: 12,
      height: 12,
      borderRadius: 6,
    },
    legendText: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultsCard: {
      backgroundColor: colors.cardBackground,
      borderRadius: 12,
      padding: 24,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    calculationSteps: {
      backgroundColor: colors.surface,
      borderRadius: 8,
      padding: 16,
      marginBottom: 20,
    },
    stepsTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    stepItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 8,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    stepLabel: {
      fontSize: 13,
      color: colors.textSecondary,
      flex: 1,
    },
    stepValue: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
    },
    resultItem: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    resultLabel: {
      fontSize: 14,
      color: colors.textSecondary,
    },
    resultValue: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
    },
    comparisonCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    cardHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    cardContent: {
      marginTop: 20,
    },
    scenarioItem: {
      marginBottom: 16,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 8,
    },
    scenarioHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 8,
    },
    scenarioName: {
      fontSize: 14,
      fontWeight: '600',
      color: colors.text,
    },
    scenarioSavings: {
      fontSize: 12,
      color: colors.success,
      fontWeight: '500',
    },
    scenarioDetails: {
      marginBottom: 8,
    },
    scenarioDetail: {
      fontSize: 12,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    scenarioExemption: {
      fontSize: 12,
      color: colors.primary,
      fontWeight: '500',
    },
    scenarioBar: {
      height: 4,
      backgroundColor: colors.border,
      borderRadius: 2,
      overflow: 'hidden',
    },
    scenarioBarFill: {
      height: '100%',
      borderRadius: 2,
    },
    documentationCard: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    docSection: {
      marginBottom: 16,
    },
    docSectionTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    docList: {
      paddingLeft: 8,
    },
    docItem: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 4,
    },
    infoContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      margin: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    seoTitle: {
      fontSize: 24,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
      textAlign: 'center',
    },
    seoSubtitle: {
      fontSize: 16,
      color: colors.primary,
      marginBottom: 16,
      textAlign: 'center',
      fontWeight: '600',
    },
    seoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 22,
      marginBottom: 16,
    },
    seoSection: {
      marginBottom: 20,
    },
    seoSectionTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 12,
    },
    comparisonContainer: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 16,
      marginTop: 12,
    },
    comparisonItem: {
      flex: 1,
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 16,
      borderWidth: 1,
      borderColor: colors.border,
    },
    comparisonTitle: {
      fontSize: 16,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    comparisonText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginBottom: 4,
    },
    featuresList: {
      marginTop: 12,
    },
    featureItem: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: 12,
      paddingVertical: 8,
    },
    featureText: {
      fontSize: 14,
      color: colors.textSecondary,
      marginLeft: 12,
      flex: 1,
    },
    stepsList: {
      marginTop: 12,
    },
    stepItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 16,
    },
    stepNumber: {
      width: 24,
      height: 24,
      borderRadius: 12,
      backgroundColor: colors.primary,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    stepNumberText: {
      fontSize: 12,
      fontWeight: 'bold',
      color: colors.background,
    },
    stepText: {
      fontSize: 14,
      color: colors.textSecondary,
      flex: 1,
      lineHeight: 20,
    },
    strategiesList: {
      marginTop: 12,
    },
    strategyItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 16,
      padding: 12,
      backgroundColor: colors.background,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border,
    },
    strategyContent: {
      marginLeft: 12,
      flex: 1,
    },
    strategyTitle: {
      fontSize: 14,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 4,
    },
    strategyText: {
      fontSize: 13,
      color: colors.textSecondary,
      lineHeight: 18,
    },
    infoTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
      marginBottom: 8,
    },
    infoSubtitle: {
      fontSize: 16,
      color: colors.primary,
      marginBottom: 16,
      fontWeight: '600',
    },
    infoText: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
      marginBottom: 16,
    },
    tipBox: {
      flexDirection: 'row',
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: 12,
      marginTop: 8,
    },
    tipText: {
      fontSize: 13,
      color: colors.textSecondary,
      marginLeft: 8,
      flex: 1,
      lineHeight: 18,
    },
    faqContainer: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      padding: 20,
      marginBottom: 20,
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.1,
      shadowRadius: 8,
      elevation: 4,
    },
    faqHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: 4,
    },
    faqTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: colors.text,
    },
    faqContent: {
      marginTop: 20,
    },
    faqItem: {
      marginBottom: 20,
      paddingBottom: 16,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
    },
    faqQuestion: {
      fontSize: 16,
      fontWeight: '600',
      color: colors.text,
      marginBottom: 8,
    },
    faqAnswer: {
      fontSize: 14,
      color: colors.textSecondary,
      lineHeight: 20,
    },
    fullWidthSection: {
      paddingHorizontal: 20,
    },
  });

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {renderHeader()}
      
      <View style={styles.content}>
        <View style={styles.leftPanel}>
          {renderCalculator()}
        </View>
        
        <View style={styles.rightPanel}>
          {renderDonutChart()}
          {renderResults()}
        </View>
      </View>
      
      {renderComparison()}
      {renderDocumentation()}
      {renderInformation()}
      {renderSEOContent()}
      
      <View style={styles.fullWidthSection}>
        {renderFAQ()}
      </View>
      
      <Footer />
    </ScrollView>
  );
};

export default HRACalculatorScreen;